import classNames from "classnames";
import sortBy from "lodash.sortby";
import React, { ReactNode } from "react";
import GetText from "../../i18n/GetText";
import { OrganisationUrlType, SanityEcosystemMember } from "../../model/common";
import { imageUrlBuilder } from "../../utils/images";
import CardContent from "../CardContent/CardContent";
import { ImageRatios } from "../Image/Image";
import ImageCard from "../ImageCard/ImageCard";
import MaybeLink from "../MaybeLink/MaybeLink";
import TagInfo from "../TagInfo/TagInfo";
import styles from "./OrganisationGrid.module.scss";
import Text from "../Text/Text";
import { organisationUrl } from "../../utils/organisation-util";

type OrganisationGridLayout = "logo" | "card" | "text";
type OrganisationGridTags = "countries" | "foundingPartnerStatus" | "none";

interface OrganisationGridProps {
  node: {
    layout: OrganisationGridLayout;
    tags: OrganisationGridTags;
    preferredUrlType: OrganisationUrlType;
    organisations: SanityEcosystemMember[];
  };
}

const OrganisationGrid = ({
  node: { organisations: unsortedOrganisations, layout, preferredUrlType, tags }
}: OrganisationGridProps) => {
  const organisations = sortBy(unsortedOrganisations, o => o.name);
  if (layout === "text") {
    return (
      <TextContainer
        organisations={organisations}
        preferredUrlType={preferredUrlType}
        tags={tags}
      />
    );
  }

  const Component = layout === "card" ? Card : Logo;
  return (
    <div className={classNames(styles.container, styles[layout])}>
      {organisations.map(org => (
        <Component
          key={org._id}
          value={org}
          preferredUrlType={preferredUrlType}
          tags={tags}
        />
      ))}
    </div>
  );
};

type OrganisationEntry = ({
  value,
  preferredUrlType,
  tags
}: {
  value: SanityEcosystemMember;
  preferredUrlType: OrganisationUrlType;
  tags: OrganisationGridTags;
}) => JSX.Element;

const TextContainer = ({
  organisations,
  preferredUrlType,
  tags
}: {
  preferredUrlType: OrganisationUrlType;
  organisations: SanityEcosystemMember[];
  tags: OrganisationGridTags;
}) => (
  <ul className={styles.text}>
    {organisations.map(o => (
      <li key={o._id}>
        <OrganisationText
          value={o}
          preferredUrlType={preferredUrlType}
          tags={tags}
        />
      </li>
    ))}
  </ul>
);

const OrganisationText: OrganisationEntry = ({
  value,
  preferredUrlType,
  tags
}) => {
  const { name, countries } = value;
  const url = organisationUrl(value, preferredUrlType);
  let tagText: ReactNode | undefined;
  if (tags === undefined || tags === "countries") {
    tagText = countries
      .map(c => c.name)
      .sort()
      .join(", ");
  } else if (tags === "foundingPartnerStatus" && value.foundingPartner) {
    tagText = <FoundingPartner />;
  }
  return (
    <Text variant="defaultLight">
      <MaybeLink to={url}>{name}</MaybeLink>
      {tagText ? `, ${tagText}` : ""}
    </Text>
  );
};

const FoundingPartner = () => (
  <GetText id="founding-partner" fallback="Founding Partner" />
);

const Logo: OrganisationEntry = ({ value, preferredUrlType }) => {
  const { logo, name } = value;
  const url = organisationUrl(value, preferredUrlType);
  const width = 260;
  const height = 100;
  return (
    <MaybeLink to={url}>
      <img
        className={styles.card}
        width={width}
        height={height}
        src={
          imageUrlBuilder
            .ignoreImageParams()
            .image(logo)
            .width(width)
            .height(height)
            .fit("fillmax")
            .bg("fff")
            .url()!
        }
        alt={name}
      />
    </MaybeLink>
  );
};

const Card: OrganisationEntry = ({ value, preferredUrlType, tags }) => {
  const { name, logo, countries } = value;
  const url = organisationUrl(value, preferredUrlType);
  let tagInfo: ReactNode | undefined;
  if (tags === undefined || tags === "countries") {
    const sortedCountries = countries ? sortBy(countries, c => c.name) : [];
    tagInfo = (
      <TagInfo
        value={[sortedCountries.map(c => <span key={c.code}>{c.name}</span>)]}
      />
    );
  } else if (tags === "foundingPartnerStatus" && value.foundingPartner) {
    tagInfo = (
      <TagInfo
        value={[
          [
            <span key="foundingPartner">
              <FoundingPartner />
            </span>
          ]
        ]}
      />
    );
  }
  return (
    <ImageCard
      className={styles.card}
      aspectRatio={ImageRatios.Landscape_4_3}
      widths={260}
      padded
      fit="fillmax"
      image={logo}
      ignoreImageParams
      href={url}
    >
      <CardContent title={name} href={url} tags={tagInfo} />
    </ImageCard>
  );
};

export default OrganisationGrid;
